import React, { useState, useEffect, useRef, version } from 'react';
import { useDS5Controller } from '../hooks/useDS5Controller';
import { useMovementCache } from '../hooks/useMovementCache';
import { api } from '../services/api';
import './QualityControlStation.css';
import packageJson from '../../package.json';
import Cadance from './Cadance';
const APP_VERSION = packageJson.version;

const QualityControlStation = ({
  userInfo,
  userPosition,
  stationName = "Quality Control",
  nextJobOnRepair = "Repair"
}) => {
  const { serialNumber, isConnected } = useDS5Controller();
  const movementsCache = useMovementCache();

  // Core states
  const [hourCount, setHourCount] = useState(0);
  const [warning, setWarning] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [nextJob, setNextJob] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [hasBeenProcessed, setHasBeenProcessed] = useState(false);
  const [selectedGrade, setSelectedGrade] = useState('');
  const [currentMovementData, setCurrentMovementData] = useState(null);
  const [paletteMismatchWarning, setPaletteMismatchWarning] = useState('');

  // Fail modal
  const [showFailModal, setShowFailModal] = useState(false);
  const [pannes, setPannes] = useState([]);
  const [selectedPanneId, setSelectedPanneId] = useState('');
  const [isOtherPanne, setIsOtherPanne] = useState(false);
  const [newPanneName, setNewPanneName] = useState('');
  const [selectedMovement, setSelectedMovement] = useState('');
  const [selectedGradeForOther, setSelectedGradeForOther] = useState('');
  const [selectedPanneInfo, setSelectedPanneInfo] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);
  const [modalError, setModalError] = useState('');

  // Palette management
  const [palettes, setPalettes] = useState([]);
  const [selectedPalette, setSelectedPalette] = useState('');
  const [paletteWarning, setPaletteWarning] = useState('');
  const [paletteSuccess, setPaletteSuccess] = useState('');
  const [isPaletteSaving, setIsPaletteSaving] = useState(false);

  // Palette action confirmation popup
  const [showPaletteConfirmModal, setShowPaletteConfirmModal] = useState(false);
  const [pendingPaletteAction, setPendingPaletteAction] = useState(null); // 'pass' | 'uncomplete' | 'fail'

  const isCheckingRef = useRef(false);
  const isCorrectStation = userPosition === stationName;

  const isLockValid = currentMovementData &&
    currentMovementData.nextjob === stationName &&
    userPosition === stationName;

  // Load palettes for Quality Control (id_mvtpal = 2)
  useEffect(() => {
    const loadPalettes = async () => {
      try {
        const data = await api.getPalette(2); // id_mvtpal = 2 for QC
        setPalettes(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Erreur chargement des palettes:', err);
        setPaletteWarning('Erreur chargement des palettes');
      }
    };

    if (isCorrectStation) loadPalettes();
  }, [isCorrectStation]);

  // ✅ Add periodic palette refresh every 10 seconds
  useEffect(() => {
    if (!isCorrectStation) return;

    const intervalId = setInterval(async () => {
      try {
        console.log('Auto-refreshing palettes from database...');
        const data = await api.getPalette(2);
        setPalettes(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error auto-refreshing palettes:', err);
      }
    }, 10000); // 10 seconds

    return () => clearInterval(intervalId);
  }, [isCorrectStation]);

  // Load pannes when fail modal opens
  useEffect(() => {
    if (!showFailModal) return;

    api.getAllDs5Panne()
      .then(data => setPannes(Array.isArray(data) ? data : []))
      .catch(err => setWarning('Erreur chargement des pannes: ' + err.message));
  }, [showFailModal]);

  // Hourly counter reset
  useEffect(() => {
    const resetHourlyCount = () => setHourCount(0);
    const now = new Date();
    const msUntilNextHour =
      (60 - now.getMinutes()) * 60 * 1000 -
      now.getSeconds() * 1000 -
      now.getMilliseconds();

    const timeoutId = setTimeout(() => {
      resetHourlyCount();
      const intervalId = setInterval(resetHourlyCount, 60 * 60 * 1000);
      return () => clearInterval(intervalId);
    }, msUntilNextHour);

    return () => clearTimeout(timeoutId);
  }, []);

  const getMovementNameById = (movementId) => {
    if (!movementsCache) return null;
    for (const [name, id] of Object.entries(movementsCache)) {
      if (id === movementId) return name;
    }
    return null;
  };

  const getNextJobNameFromId = (nextJobId) => nextJobId ? getMovementNameById(nextJobId) : null;

  // ✅ NEW: Check if controller belongs to selected palette
  useEffect(() => {
    if (!selectedPalette || !currentMovementData || !serialNumber) {
      setPaletteMismatchWarning('');
      return;
    }

    // Check if the controller's palette ID matches the selected palette
    const controllerPaletteId = currentMovementData.id_palette;
    const selectedPaletteId = Number(selectedPalette);

    if (controllerPaletteId && controllerPaletteId !== selectedPaletteId) {
      const selectedPaletteObj = palettes.find(p => p.id === selectedPaletteId);
      const controllerPaletteObj = palettes.find(p => p.id === controllerPaletteId);
      
      setPaletteMismatchWarning(
        `⚠️ PALETTE MISMATCH: Controller belongs to palette "${controllerPaletteObj?.sn_palette ||""}", ` +
        `but you selected palette "${selectedPaletteObj?.sn_palette || selectedPaletteId}"`
      );
    } else {
      setPaletteMismatchWarning('');
    }
  }, [selectedPalette, currentMovementData, serialNumber, palettes]);

  // Current movement check
  useEffect(() => {
    if (!isConnected || !serialNumber || serialNumber.includes('Error')) {
      setWarning('');
      setCurrentMovementData(null);
      setHasBeenProcessed(false);
      setSelectedGrade('');
      setPaletteMismatchWarning('');
      return;
    }
    if (!movementsCache || Object.keys(movementsCache).length === 0) return;

    const checkMovementHistory = async () => {
      if (isCheckingRef.current) return;
      isCheckingRef.current = true;
      try {
        const data = await api.getCurrentMovement(serialNumber);
        if (!data) {
          setWarning("Controller not in the database - Start with Quick Drift");
          setHasBeenProcessed(false);
          setPaletteMismatchWarning('');
          return;
        }

        const processedData = {
          ...data,
          movement: getMovementNameById(data.id_movement),
          nextjob: getMovementNameById(data.id_nextjob),
        };

        setCurrentMovementData(processedData);

        if (processedData.nextjob !== stationName) {
          setWarning(`Next_Job : ${processedData.nextjob || 'Aucun'}`);
          setHasBeenProcessed(false);
          setPaletteMismatchWarning('');
          return;
        }

        if (userPosition !== stationName) {
          setWarning(`Position incorrecte: ${userPosition} - Doit être "${stationName}"`);
          setHasBeenProcessed(false);
          setPaletteMismatchWarning('');
          return;
        }

        setWarning('');
      } catch (err) {
        setWarning(err.message.includes('404') ?
          "Controller not in database - Start with Quick Drift" :
          "Erreur de vérification de l'historique");
        setHasBeenProcessed(false);
        setPaletteMismatchWarning('');
      } finally {
        isCheckingRef.current = false;
      }
    };

    checkMovementHistory();
  }, [isConnected, serialNumber, movementsCache, userPosition, stationName]);

  // Save to DB
  const saveToDatabase = async (grade, targetNextJob, panneId) => {
    if (!isLockValid) return false;

    // ✅ Check palette mismatch before saving
    if (paletteMismatchWarning && targetNextJob === "Export") {
      setWarning('Cannot save: Palette mismatch. Please select the correct palette.');
      return false;
    }

    let movementId = movementsCache[stationName];
    let nextJobId = movementsCache[targetNextJob];

    // ✅ Grade mismatch
    if (
      currentMovementData?.Grade &&
      grade !== currentMovementData.Grade &&
      targetNextJob === "Export"
    ) {
      nextJobId = 6; // Packaging
      targetNextJob = "Packaging";
    }

    // ✅ If fail → Repair
    if (panneId || targetNextJob === "Repair") {
      nextJobId = movementsCache[nextJobOnRepair];
      targetNextJob = nextJobOnRepair;
    }

    const payload = {
      serial_number: serialNumber,
      operator: userInfo?.id || 1,
      tester: stationName,
      movement: movementId,
      nextjob: nextJobId,
      Grade: grade,
      panne: panneId ? Number(panneId) : null,
      version: APP_VERSION,
      color: currentMovementData?.color || null,
      id_palette: selectedPalette
    };

   
    // ✅ Save the current palette ID to restore it after reload
    const currentPaletteId = selectedPalette;

    try {
      setIsSaving(true);

      await api.saveMovement(payload);

      // ✅ Reload palettes to get updated quantities
      console.log('Reloading palettes after controller save...');
      const updatedPalettes = await api.getPalette(2);
      console.log(palettes)
      setPalettes(updatedPalettes.filter(p => p.id_mvtpal === 2));

      // ✅ Restore the selected palette if it still exists
      if (currentPaletteId) {
        const paletteStillExists = updatedPalettes.find(p => p.id === Number(currentPaletteId));
        if (paletteStillExists) {
          setSelectedPalette(currentPaletteId);
          console.log(`Palette ${currentPaletteId} restored after save`);
        } else {
          setSelectedPalette('');
          console.log(`Palette ${currentPaletteId} no longer available`);
        }
      }

      setNextJob(targetNextJob);
      setShowSuccess(true);
      setHasBeenProcessed(true);
      setWarning('');
      setPaletteMismatchWarning('');
      setHourCount(prev => prev + 1);

      return true;
    } catch (err) {
      console.error(err);
      setWarning("Erreur enregistrement");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handlePaletteReturnToQC = async () => {
    if (!selectedPalette) return setPaletteWarning('Veuillez sélectionner une palette');
    setIsPaletteSaving(true);
    setPaletteWarning('');
    
    // ✅ Save the current palette ID to restore it after reload
    const currentPaletteId = selectedPalette;
    
    try {
      await api.updatePalette(selectedPalette, 1); // Return to QC
      // await api.updatehistoryPalette(selectedPalette, 6, 7, stationName, userInfo.id, version);
      setPaletteSuccess('Palette return to embalage');
      
      // ✅ Reload palettes after update
      const updatedPalettes = await api.getPalette(2);
      setPalettes(updatedPalettes);
      
      // ✅ Check if the palette still exists in the updated list
      const paletteStillExists = updatedPalettes.find(p => p.id === Number(currentPaletteId));
      if (paletteStillExists) {
        setSelectedPalette(currentPaletteId); // Keep it selected
      } else {
        setSelectedPalette(''); // Clear if it no longer exists
      }
    } catch (err) {
      setPaletteWarning('Erreur retour palette: ' + err.message);
    } finally { 
      setIsPaletteSaving(false); 
    }
  };

  const decrementPaletteQuantity = async (paletteId) => {
    try {
      const palette = palettes.find(p => p.id === Number(paletteId));
      if (!palette) return;

      const newCapacity = Math.max(0, palette.capacity - 1);
      await api.updatePalette(paletteId, newCapacity);

      const updatedPalettes = await api.getPalette(2);
      setPalettes(updatedPalettes);
    } catch (err) {
      console.error('Error decrementing palette quantity:', err);
    }
  };

  // Palette pass/fail handlers
  const handlePalettePass = async () => {
    if (!selectedPalette) return setPaletteWarning('Veuillez sélectionner une palette');
    setIsPaletteSaving(true); 
    setPaletteWarning('');
    
    // ✅ Save the current palette ID to restore it after reload
    const currentPaletteId = selectedPalette;
    
    try {
      await api.updatePalette(selectedPalette, 4);
      await api.updatehistoryPalette(selectedPalette, 8, 7, stationName, userInfo.id, APP_VERSION);
      setPaletteSuccess('Palette validée avec succès');
      setTimeout(() => setPaletteSuccess(''), 3000);
      
      // ✅ Reload palettes after update
      const updatedPalettes = await api.getPalette(2);
      setPalettes(updatedPalettes);
      
      // ✅ Check if the palette still exists in the updated list
      const paletteStillExists = updatedPalettes.find(p => p.id === Number(currentPaletteId));
      if (paletteStillExists) {
        setSelectedPalette(currentPaletteId); // Keep it selected
      } else {
        setSelectedPalette(''); // Clear if it no longer exists
      }
    } catch (err) {
      setPaletteWarning('Erreur validation palette: ' + err.message);
    } finally { 
      setIsPaletteSaving(false); 
    }
  };

  const handlePaletteFail = async () => {
    if (!selectedPalette) return setPaletteWarning('Veuillez sélectionner une palette');
    setIsPaletteSaving(true); 
    setPaletteWarning('');
    
    // ✅ Save the current palette ID to restore it after reload
    const currentPaletteId = selectedPalette;
    
    try {
      await api.updatePalette(selectedPalette, 3);
      await api.updatehistoryPalette(selectedPalette, 14, 7, stationName, userInfo.id, APP_VERSION);
      setPaletteSuccess('Palette mise en échec');
      setTimeout(() => setPaletteSuccess(''), 3000);
      
      // ✅ Reload palettes after update
      const updatedPalettes = await api.getPalette(2);
      setPalettes(updatedPalettes);
      
      // ✅ Check if the palette still exists in the updated list
      const paletteStillExists = updatedPalettes.find(p => p.id === Number(currentPaletteId));
      if (paletteStillExists) {
        setSelectedPalette(currentPaletteId); // Keep it selected
      } else {
        setSelectedPalette(''); // Clear if it no longer exists
      }
    } catch (err) {
      setPaletteWarning('Erreur mise en échec palette: ' + err.message);
    } finally { 
      setIsPaletteSaving(false); 
    }
  };

  // Palette action — requires DualSense to be unplugged first
  const requestPaletteAction = (action) => {
    setPendingPaletteAction(action);
    setShowPaletteConfirmModal(true);
  };

  const cancelPaletteAction = () => {
    setShowPaletteConfirmModal(false);
    setPendingPaletteAction(null);
  };

  // Auto-execute the action as soon as the controller is unplugged while popup is open
  useEffect(() => {
    if (!showPaletteConfirmModal || !pendingPaletteAction) return;
    if (isConnected) return; // still plugged in — wait

    const execute = async () => {
      setShowPaletteConfirmModal(false);
      if (pendingPaletteAction === 'pass') await handlePalettePass();
      else if (pendingPaletteAction === 'uncomplete') await handlePaletteReturnToQC();
      else if (pendingPaletteAction === 'fail') await handlePaletteFail();
      setPendingPaletteAction(null);
    };

    execute();
  }, [isConnected, showPaletteConfirmModal, pendingPaletteAction]);

  // ✅ Always get fresh palette data from the palettes array
  const selectedPaletteObj = palettes.find(p => p.id === Number(selectedPalette));

  // Reset on serial change
  useEffect(() => {
    setShowSuccess(false);
    setNextJob('');
    setCurrentMovementData(null);
    setWarning('');
    setHasBeenProcessed(false);
    setSelectedGrade('');
    setPaletteMismatchWarning('');
  }, [serialNumber]);

  if (!isCorrectStation) return null;

  const pendingActionLabel =
    pendingPaletteAction === 'pass' ? 'PASS' :
    pendingPaletteAction === 'uncomplete' ? 'UNCOMPLETE' :
    pendingPaletteAction === 'fail' ? 'FAIL' : '';

  return (
    <div className="QualityControl-container">
      <div className="main-content">
        <div className="QualityControl-header">
          <h1>{stationName}</h1>
        </div>

        {!isConnected ? (
          <div className="disconnected">
            <h1>🔌 Plug in your DualSense controller.</h1>
          </div>
        ) : (
          <>
            <div className="serial-display">
              <label>Serial Number:</label>
              <div className="serial-value">{serialNumber}</div>
            </div>

            {/* ✅ Display controller's palette info */}
            {currentMovementData?.id_palette && (
              <div className="controller-palette-info">
                <label>Controller's Palette:</label>
                <div className="palette-value">
                  {palettes.find(p => p.id === currentMovementData.id_palette)?.sn_palette || 
                   `ID: ${currentMovementData.id_palette}`}
                </div>
              </div>
            )}

            {warning && <div className="message warning-message">{warning}</div>}
            {paletteMismatchWarning && (
              <div className="message error-message palette-mismatch">
                {paletteMismatchWarning}
              </div>
            )}
            {isSaving && <div className="message waiting-message">Enregistrement...</div>}

            {showSuccess && nextJob && (
              <div className={`message success-message permanent ${nextJob === 'Repair' ? 'fail' : 'pass'}`}>
                <div><strong>Next Job:</strong> {nextJob}</div>
                {selectedGrade !== '-' && <div><strong>Grade:</strong> {selectedGrade}</div>}
              </div>
            )}

            {!hasBeenProcessed && isLockValid && !isSaving && !showSuccess && userInfo && (
              <div className="buttons-section">
                <div className="grade-buttons-row">
                  {['A', 'B', 'C'].map(g => (
                    <button
                      key={g}
                      onClick={() => setSelectedGrade(g)}
                      className={`btn-grade grade-${g.toLowerCase()} ${selectedGrade === g ? 'selected' : ''}`}
                    >{g}</button>
                  ))}
                </div>

                <div className="action-buttons-row">
                  <button
                    onClick={() => saveToDatabase(selectedGrade, 'Export', null)}
                    disabled={!selectedGrade || isSaving || !!paletteMismatchWarning || !selectedPalette}
                    className="btn-validate-grade"
                  >
                    VALIDATE GRADE
                  </button>
                  <button
                    onClick={() => saveToDatabase(selectedGrade, 'Repair', null)}
                    disabled={isSaving|| !!paletteMismatchWarning || !selectedPalette}
                    className="btn-fail"
                  >
                    FAIL
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Palette Section */}
      <div className="palette-section">
        <h2>Palette</h2>
        <div className="palette-field">
          <select
            value={selectedPalette}
            onChange={e => { 
              setSelectedPalette(e.target.value); 
              setPaletteWarning(''); 
              setPaletteSuccess(''); 
            }}
            disabled={isPaletteSaving}
          >
            <option value="">-- Choisir une palette --</option>
            {palettes.map(p => {
              const quantity = p.qty || 0;
              const capacity = p.capacity || 0;
              const available = capacity - quantity;
              
              return (
                <option key={p.id} value={p.id}>
                  {p.sn_palette}
                </option>
              );
            })}
          </select>
        </div>

        <table className="palette-table">
          <thead>
            <tr>
              <th>SN Palette</th>
              <th>Grade</th>
              <th>Couleur</th>
              <th>Qté</th>
              <th>Cap.</th>
              <th>Statut</th>
            </tr>
          </thead>
          <tbody>
            {selectedPaletteObj && (
              <tr className="selected-row">
                <td>{selectedPaletteObj.sn_palette}</td>
                <td>
                  <span className={`grade-badge grade-${selectedPaletteObj.grad.toLowerCase()}`}>
                    {selectedPaletteObj.grad}
                  </span>
                </td>
                <td>{selectedPaletteObj.color}</td>
                <td>{selectedPaletteObj.qty || 0}</td>
                <td>{selectedPaletteObj.capacity || 0}</td>
                <td>
                  <span className={`status-badge status-${selectedPaletteObj.id_mvtpal}`}>
                    {selectedPaletteObj.status_name}
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {paletteWarning && <div className="message warning-message palette-message">{paletteWarning}</div>}
        {paletteSuccess && <div className="message success-message palette-message">{paletteSuccess}</div>}

        <div className="palette-buttons">
          <button 
            onClick={() => requestPaletteAction('pass')} 
            disabled={!selectedPalette || isPaletteSaving || selectedPaletteObj.qty < selectedPaletteObj.capacity} 
            className="btn-palette-pass"
          >
            PASS
          </button>
          <button
            onClick={() => requestPaletteAction('uncomplete')}
            disabled={!selectedPalette || isPaletteSaving || selectedPaletteObj.qty === selectedPaletteObj.capacity}
            className="btn-palette-return"
          >
            Uncomplete
          </button>
          <button 
            onClick={() => requestPaletteAction('fail')} 
            disabled={!selectedPalette || isPaletteSaving || selectedPaletteObj.qty === selectedPaletteObj.capacity} 
            className="btn-palette-fail"
          >
            FAIL
          </button>
        </div>
      </div>

      {/* Palette Action — Unplug Info Modal */}
      {showPaletteConfirmModal && (
        <div className="palette-popup-overlay">
          <div className="palette-confirmation-dialog" style={{ position: 'relative' }}>
            <button onClick={cancelPaletteAction} className="btn-popup-close">✕</button>
            <div className="confirmation-header">
              <h3>🎮 Unplug your DualSense</h3>
            </div>
            <div className="confirmation-details">
              <p>You must <strong>unplug your DualSense controller</strong> to proceed.</p>
              <p><strong>Action:</strong> {pendingActionLabel}</p>
              <p><strong>Palette:</strong> {selectedPaletteObj?.sn_palette || selectedPalette}</p>
             
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QualityControlStation;